"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-3 py-2 text-sm font-medium tracking-wide text-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent transition-colors font-default" style={{
            letterSpacing: "0.04em"
          }}>
                <span className="relative">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-[2px] w-0 bg-[hsl(45,90%,45%)] transition-all duration-[220ms] ease-out group-hover:w-full" aria-hidden="true" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-2">
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="group flex items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-accent transition-colors font-default">
          <span className="relative">
            {item.label}
            <span className="pointer-events-none absolute -bottom-1 left-0 h-[2px] w-0 bg-[hsl(45,90%,45%)] transition-all duration-[220ms] ease-out group-hover:w-full" aria-hidden="true" />
          </span>
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
            №/0{i + 1}
          </span>
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1803" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-[240ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "bg-background border-b border-transparent"}`}>
      {scrolled && <span aria-hidden="true" className="pointer-events-none absolute -bottom-[3px] right-4 h-[6px] w-[6px] rounded-lg bg-[hsl(45,90%,45%)]" />}

      <div className="mx-auto w-full max-w-[1360px] px-6 md:px-10 lg:px-16">
        <div className="grid h-[68px] md:h-[84px] grid-cols-12 items-center gap-4">
          <div className="col-span-8 md:col-span-9 flex items-center gap-6 md:gap-12 lg:gap-20 min-w-0">
            <Link to="/" className="flex items-center shrink-0" aria-label="Little Acorns Montessori — Home">
              <WvcLogo className="h-10 w-auto" />
            </Link>

            <div className="hidden lg:flex items-center min-w-0">
              <MenuProvider menu_id="2">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-3 md:gap-5">
            <div className="hidden md:flex items-center gap-1.5 font-mono text-[11px] uppercase text-foreground" style={{
            letterSpacing: "0.18em"
          }}>
              <span className="text-muted-foreground">EST. 2005</span>
              <span className="text-[hsl(45,90%,45%)] text-sm leading-none" aria-hidden="true">
                *
              </span>
              <span>PARKWOODS</span>
            </div>

            <Button asChild className="hidden md:inline-flex h-12 rounded-lg px-8 text-sm font-semibold tracking-wide bg-[hsl(145,30%,12%)] text-[hsl(40,33%,96%)] hover:bg-[hsl(145,30%,20%)] shadow-md hover:-translate-y-0.5 transition-all duration-200 font-default" style={{
            letterSpacing: "0.04em",
            borderRadius: "9999px"
          }}>
              <Link to="/ContactUsPage">Book a Tour</Link>
            </Button>

            <div className="md:hidden flex items-center gap-1 rounded-lg bg-[hsl(45,90%,45%)]/10 px-2.5 py-1 font-mono text-[10px] uppercase text-[hsl(145,30%,12%)]" style={{
            letterSpacing: "0.14em",
            borderRadius: "9999px"
          }}>
              <span aria-hidden="true">*</span>
              <span>TOUR</span>
            </div>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden h-10 w-10 rounded-lg border-border bg-background text-foreground hover:bg-accent" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm bg-background p-0 flex flex-col">
                <SheetHeader className="px-6 pt-6 pb-2 border-b border-border">
                  <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase text-foreground" style={{
                  letterSpacing: "0.18em"
                }}>
                    <span className="text-muted-foreground">LITTLE ACORNS</span>
                    <span className="text-[hsl(45,90%,45%)] text-sm leading-none" aria-hidden="true">
                      *
                    </span>
                    <span>MENU</span>
                  </div>
                  <SheetTitle className="font-default text-3xl font-normal text-foreground tracking-tight leading-tight">
                    Welcome to{" "}
                    <span className="italic relative inline-block font-serif">
                      Little
                      <span aria-hidden="true" className="absolute left-0 -bottom-1 h-[2.5px] w-full bg-[hsl(45,90%,45%)] rounded-lg" />
                    </span>{" "}
                    Acorns
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto py-4">
                  <MenuProvider menu_id="2">
                    <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="px-6 pb-6 pt-4 border-t border-border space-y-3">
                  <Button asChild className="w-full h-12 text-sm font-semibold tracking-wide bg-[hsl(145,30%,12%)] text-[hsl(40,33%,96%)] hover:bg-[hsl(145,30%,20%)] font-default" style={{
                  letterSpacing: "0.04em",
                  borderRadius: "9999px"
                }} onClick={() => setMobileOpen(false)}>
                    <Link to="/ContactUsPage">Book a Tour</Link>
                  </Button>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground text-center" style={{
                  letterSpacing: "0.18em"
                }}>
                    Parkwoods-Donalda  *  Est. 2005
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}